import { SongFormData } from 'app/components/Song/SongForm/types';
import { SongStatusLabel, SongStatusType } from 'types/song';
import { AnyObjectSchema } from 'yup';
import { Option } from 'types/general.types';
import { useState, FC } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { ChakraFormBaseInput, ContentContainer } from 'app/components/Core';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import { getDirtyValues } from 'app/utils/utils';
import { ContentContainerType } from 'types/enums';
import { SongCover } from 'app/components/Song/SongCover';
import { SongStatusInput } from 'app/components/Song/SongStatusInput';
import { Box, Flex, Text, Tooltip } from '@chakra-ui/react';
import { AudioActionButton } from '../Audio/AudioActionButton';

interface SongFormComponentProps {
  songFormData: SongFormData;
  onSongUpdate: (data: Partial<SongFormData>) => Promise<void>;
  onSongStatusChange: (status: SongStatusType) => void;
  validationSchema?: AnyObjectSchema;
  handlePlay: () => Promise<void>;
  isPlaying: boolean;
  isLoading?: boolean;
  canPlayLatest: boolean;
  maxTitleLength?: number;
}

const statusOptions: Option[] = Object.values(SongStatusType).map((status) => ({
  title: SongStatusLabel[status],
  value: status,
}));

export const SongFormComponent: FC<SongFormComponentProps> = ({
  songFormData,
  onSongUpdate,
  onSongStatusChange,
  isPlaying,
  isLoading = false,
  handlePlay,
  canPlayLatest,
  validationSchema,
  maxTitleLength,
}) => {
  const {
    handleSubmit,
    control,
    formState,
    reset,
    watch,
    register,
    setValue,
  } = useForm<SongFormData>({
    defaultValues: songFormData,
    mode: 'all',
    resolver: validationSchema ? yupResolver(validationSchema) : undefined,
  });

  const watchedValues = watch();
  const { dirtyFields, errors, isValid } = formState;
  const [lastValidTitle, setLastValidTitle] = useState(songFormData.title);
  const onSubmit = handleSubmit(async (data: SongFormData) => {
    reset(data);
    const dirtyValues = getDirtyValues<SongFormData>(dirtyFields, data);
    if (Object.keys(dirtyValues).length) {
      await onSongUpdate(dirtyValues);
    }
  });

  const updateStatus = async (data: SongFormData) => {
    reset(data);
    await onSongUpdate(data);
  };

  return (
    <ContentContainer
      type={ContentContainerType.LIST}
      flex={0}
      data-testid="song-header"
      position="relative"
      minH="168px"
      pt={6}
      pb={0}
      px={4}
    >
      <Flex zIndex="sticky">
        <SongCover status={watchedValues.status} />
        <Flex
          direction="column"
          w="full"
          alignItems="flex-start"
          justifyContent="center"
          ml={4}
        >
          <Text fontSize="xxs" fontWeight={500} opacity={0.72}>
            Song
          </Text>
          <Box position="relative">
            <ChakraFormBaseInput
              placeholder="Title"
              {...register('title')}
              isInvalid={Boolean(errors?.title)}
              errorMessage={errors?.title?.message}
              data-testid="song-title"
              onChange={(e) => {
                let value = e.target.value.trimStart();
                if (maxTitleLength && value.length > maxTitleLength) {
                  value = value.substring(0, maxTitleLength);
                }
                setValue('title', value, {
                  shouldDirty: true,
                  shouldValidate: true,
                });
                if (value) {
                  setLastValidTitle(value);
                }
              }}
              onBlur={(e) => {
                if (!e.target.value) {
                  setValue('title', lastValidTitle, {
                    shouldValidate: true,
                    shouldDirty: false,
                    shouldTouch: true,
                  });
                }
                if (isValid) {
                  onSubmit();
                }
              }}
              fontSize="32px"
              fontWeight={500}
            />
            {maxTitleLength &&
              maxTitleLength - watchedValues.title.length <= 30 && (
                <Text position="absolute" right={0}>
                  {watchedValues.title.length}/{maxTitleLength}
                </Text>
              )}
          </Box>
          <Flex mt={2} gap={4}>
            <Tooltip
              hasArrow
              label={
                canPlayLatest
                  ? null
                  : 'Upload an audio file and mark as Latest version'
              }
              shouldWrapChildren
            >
              <AudioActionButton
                playing={isPlaying}
                onClick={handlePlay}
                data-testid="audio-play-pause-button"
                isLoading={isLoading}
                size={32}
                isPlayerTheme
                playIconTestId="audio-play-icon"
                pauseIconTestId="audio-pause-icon"
                thickness={2}
                isDisabled={!canPlayLatest}
              />
            </Tooltip>
            <Controller
              name="status"
              control={control}
              render={({ field }) => (
                <SongStatusInput
                  value={field.value}
                  options={statusOptions}
                  onChange={(status) => {
                    if (watchedValues.status !== status) {
                      field.onChange(status);
                      onSongStatusChange(status as SongStatusType);
                      updateStatus({
                        ...watchedValues,
                        status: status as SongStatusType,
                      });
                    }
                  }}
                />
              )}
            />
          </Flex>
        </Flex>
      </Flex>
    </ContentContainer>
  );
};
